<template>
  <div id="block_list_section" class="flex flex-col gap-6 proxima_nova text_color">

    <div class="flex justify-between items-center">
      <h1 class="text-xl font-semibold text_color">Block List</h1>
      <div class="flex items-center gap-4">
        <CustomInput
          v-if="activeTab === 0"
          format="text"
          v-model="searchQuery"
          placeholder="Search numbers..."
          type="primary"
          :icon="imageNames.SEARCH_VARIANT1"
          class="w-[207px]"
          alt="search_icon"
        />
        <a-button class="primary_medium"
                  @click="handleAddToBlockList">
          Add to block list
        </a-button>
      </div>
    </div>

    <CustomTabs
      v-model="activeTab"
      :tabs="tabConfig"
    >
      <template #tab_content-0>
        <PhoneNumberBlock :search-query="searchQuery" ref="phoneNumberBlockRef" />
      </template>
      <template #tab_content-1>
        <EmailDomainBlock />
      </template>
    </CustomTabs>

    <CustomModal
        v-model:visible="showDomainModal"
        title="Add Domain/Email to Block List"
        width="560px"
        @close="formRef?.clearForm()"
    >
      <BlockListForm ref="formRef" @submit="handleAddDomainToBlockList" :disabled="modalLoading" />

      <template #footer>
        <div class="flex justify-end gap-3">
          <a-button
              class="secondary_medium"
              @click="showDomainModal = false"
              :disabled="modalLoading"
          >
            Cancel
          </a-button>
          <a-button
              :disabled="modalLoading"
              :loading="modalLoading"
              @click="formRef?.handleSubmit()"
              class="primary_medium"
          >
            <span>Add to block list</span>
          </a-button>
        </div>
      </template>
    </CustomModal>

    <AddPhoneToBlockList
      :source="'block_list'"
      :drawer-visible="showPhoneDrawer"
      @update:modelValue="showPhoneDrawer = $event"
      @complete="handlePhoneBlockListComplete"
    />
  </div>
</template>

<script setup>
import CustomModal from "@/src/components/CustomModal.vue";
import CustomTabs from "@/src/components/CustomTabs.vue";
import BlockListForm from "@/src/components/block_list_components/BlockListForm.vue";
import PhoneNumberBlock from "@/src/components/block_list_components/PhoneNumberBlock.vue";
import EmailDomainBlock from "@/src/components/block_list_components/EmailDomainBlock.vue";
import AddPhoneToBlockList from "@/src/views/block_lists/AddPhoneToBlockList.vue";
import CustomInput from "@/src/components/CustomInput.vue";
import { ref, computed, watch } from "vue";
import DashboardService from "../../api/DashboardService.js";
import { message } from "ant-design-vue";
import { imageNames } from "@/src/constants/imageConstants.js";

const modalLoading = ref(false);
const showDomainModal = ref(false);
const showPhoneDrawer = ref(false);
const formRef = ref(null);
const activeTab = ref(0);
const searchQuery = ref("");
const phoneNumberBlockRef = ref(null);

const tabConfig = computed(() => [
  { title: "Phone Numbers" },
  { title: "Domain/Email" }
]);

const handleTabChange = (tabIndex) => {
  activeTab.value = tabIndex;
};

const handleAddToBlockList = () => {
  if (activeTab.value === 1) { 
    showDomainModal.value = true;
  } else { 
    showPhoneDrawer.value = true;
  }
};

const handlePhoneBlockListComplete = (success) => {
  if (success && phoneNumberBlockRef.value) {
    phoneNumberBlockRef.value.fetchPhoneBlockList();
  }
};

const handleAddDomainToBlockList = async (value) => {
  try {
    modalLoading.value = true;
    console.log("Adding to block list:", value);
    let type = value.includes('@') ? 'email' : 'domain';

    const response = await DashboardService.addEmailAndDomainBlocklist({ type, value });

    if (response?.success) {
      message.success("Added to block list successfully");
      formRef.value?.clearForm();
      showDomainModal.value = false;
    } else {
      message.error("Failed to add to block list");
    }
  } catch (error) {
    console.error("Error adding to block list:", error);
    message.error(error.response?.data?.error || "Failed to add to block list");
  } finally {
    modalLoading.value = false;
  }
};
</script>

<style scoped>
.proxima_nova {
  font-family: 'Proxima Nova', sans-serif;
}

/* Dark theme custom table styles */
:deep(.custom-table) {
  background: #1A1A1A;
  border-radius: 12px;
  overflow: hidden;
}

:deep(.table-header) {
  background: #1F1F1F;
  color: #A0A0A0;
}

:deep(.table-row) {
  border-bottom: 1px solid #2A2A2A;
}

:deep(.table-row:hover) {
  background: #1F1F1F;
}
</style>